import React from 'react';
import PropTypes from 'prop-types';
/* helpers */
import { httpRequest } from '../../utils/xml-request-helper';
import { sortItemsByPropertyName } from '../../utils/info-helper';
/* components */
import EmptyContent from '../../components/empty-content/empty-content';
import FixedHeaderTable from '../../components/fixed-header-table/fixed-header-table';
import Menu, { MenuItem } from '../../components/menu/menu';
import Modal from '../../components/modal/modal';
import TableHeadCell from '../../components/table-head-cell/table-head-cell';
/* styles */
import './clients.scss';

const tableColumns = [
    { title: 'Client Id', prop: 'clientId', sortable: true, width: 14, withMenu: true },
    { title: 'Client name', prop: 'name', sortable: true, width: 14 },
    { title: 'Client secret', prop: 'clientSecret', sortable: true, width: 14, className: 'text-ellipsis' },
    {
        title: 'Local login enabled',
        prop: 'enableLocalLogin',
        sortable: true,
        width: 10,
        type: 'bool'
    },
    { title: 'Sign-in redirect URLs', prop: 'redirectUris', width: 28, type: 'list' },
    { title: 'Sign-out redirect URLs', prop: 'postLogoutRedirectUris', width: 28, type: 'list' },
    { title: 'Scopes', prop: 'scopes', width: 12, type: 'list' },
    { title: 'Grant types', prop: 'allowedGrantTypes', width: 12, type: 'list' },
    {
        title: 'Require consent',
        prop: 'requireConsent',
        sortable: true,
        width: 10,
        type: 'bool'
    },
    {
        title: 'Access token lifetime',
        prop: 'accessTokenLifetimeSeconds',
        sortable: true,
        width: 10,
        unit: 's'
    },
    {
        title: 'Refresh token lifetime',
        prop: 'absoluteRefreshTokenLifetimeSeconds',
        sortable: true,
        width: 10,
        unit: 's'
    },
    {
        title: 'Allow offline access',
        prop: 'allowOfflineAccess',
        sortable: true,
        width: 10,
        type: 'bool'
    },
    {
        title: 'Reuse refresh token',
        prop: 'reUseRefreshToken',
        sortable: true,
        width: 10,
        type: 'bool'
    },
    {
        title: 'Update access token claims on refresh',
        prop: 'updateAccessTokenClaimsOnRefresh',
        sortable: true,
        width: 12,
        type: 'bool'
    },
    { title: 'Allow PKCE', prop: 'requirePkce', sortable: true, width: 10, type: 'bool' }
];

const contentStyle = { width: `${tableColumns.reduce((res, el) => res + el.width, 0)}rem` };

class ClientsPage extends React.PureComponent {
    state = {
        sortedClients: [...this.props.clients],
        sortProp: '',
        sortOrder: 'ASC',
        isDeleteModalVisible: false,
        deleteClientId: null
    };

    setSortProp = (prop) => {
        let sortProp = this.state.sortProp;
        let sortOrder = this.state.sortOrder;
        if (prop === this.state.sortProp) {
            sortOrder = this.state.sortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
            sortProp = prop;
            sortOrder = 'ASC';
        }
        const sortedClients = this.props.clients.slice().sort(sortItemsByPropertyName(sortProp, sortOrder === 'ASC'));
        this.setState({ sortProp, sortOrder, sortedClients });
    };

    renderCellValue = (col, value) => {
        if (col.type === 'bool') {
            return value[col.prop]
                ? <span className="sten-badge sten-badge--success">Yes</span>
                : <span className="sten-badge sten-badge--danger">No</span>;
        }
        if (col.type === 'list') {
            if (!value[col.prop]
                || value[col.prop].length === 0
                || (value[col.prop].length === 1 && !value[col.prop][0])) {
                return '-';
            }
            /* eslint-disable-next-line react/no-array-index-key */
            return <ul className="sten-ul">{value[col.prop].map((item, index) => <li key={index}>{item}</li>)}</ul>;
        }
        if (!value[col.prop]) {
            return '-';
        }
        return `${value[col.prop]}${col.unit || ''}`;
    };

    handleDelete = (clientId) => {
        this.setState({ isDeleteModalVisible: true, deleteClientId: clientId });
    };

    handleDeleteConfirm = () => {
        httpRequest({
            method: 'DELETE',
            url: `/clients/delete/${this.state.deleteClientId}`,
            onSuccess: () => window.location.reload()
        });
        this.handleDeleteCancel();
    };

    handleDeleteCancel = () => {
        this.setState({ isDeleteModalVisible: false, deleteClientId: null });
    };

    renderMenu = (client) => (
        <Menu menuRight>
            <MenuItem linkTo={`/clients/edit/${client.clientId}`} icon="icon-edit">
                Edit
            </MenuItem>
            <MenuItem onClick={this.handleDelete.bind(this, client.clientId)} icon="icon-delete">
                Delete
            </MenuItem>
        </Menu>
    );

    render() {
        const { sortProp, sortOrder, sortedClients } = this.state;
        return (
            <div className="sten-content sten-clients-page">
                <Modal.Delete
                    isOpen={this.state.isDeleteModalVisible}
                    onDelete={this.handleDeleteConfirm}
                    onCancel={this.handleDeleteCancel}
                >
                    Are you sure you want to delete this client?
                </Modal.Delete>
                <div className="sten-content__header flex-row">
                    <div className="flex-grow">
                        <h1 className="sten-content__title text-uppercase">Clients</h1>
                    </div>
                    <div className="flex-shrink">
                        <a className="btn btn--primary" href="/clients/create">Create new client</a>
                    </div>
                </div>
                <div className="sten-content__body">
                    {sortedClients.length > 0
                        ? (
                            <FixedHeaderTable
                                updateTimeout={200}
                                contentStyle={contentStyle}
                                customFirstChild={TableHeadCell}
                                withHeaderColumn
                            >
                                <table className="sten-table sten-table--xs">
                                    <thead>
                                        <tr>
                                            {tableColumns.map((col, index) => (
                                                <TableHeadCell
                                                    style={{ width: `${col.width}rem` }}
                                                    sortOrder={sortProp === col.prop ? sortOrder : ''}
                                                    sortable={col.sortable}
                                                    /* eslint-disable-next-line react/no-array-index-key */
                                                    key={index}
                                                    onClick={this.setSortProp.bind(this, col.prop)}
                                                >
                                                    {col.title}
                                                </TableHeadCell>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedClients.map((client) => (
                                            <tr key={client.clientId}>
                                                {tableColumns.map((col, index) => (
                                                    /* eslint-disable-next-line react/no-array-index-key */
                                                    <td key={index} className={col.className}>
                                                        {col.withMenu
                                                            ? (
                                                                <div className="flex-row flex-center">
                                                                    <div className="flex-grow">
                                                                        {this.renderCellValue(col, client)}
                                                                    </div>
                                                                    <div className="flex-shrink">
                                                                        {this.renderMenu(client)}
                                                                    </div>
                                                                </div>
                                                            )
                                                            : this.renderCellValue(col, client)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </FixedHeaderTable>
                        ) : <EmptyContent />
                    }
                </div>
            </div>
        );
    }
}

ClientsPage.propTypes = {
    clients: PropTypes.arrayOf(PropTypes.object).isRequired,
    token: PropTypes.string.isRequired
};

export default ClientsPage;
